import type { CloudCostInvoice, CloudCostReport } from "@hasheeai/types";
import { useState } from "react";
import { t } from "../../i18n";
import * as s from "../../styles/shared";
import { CostHelp } from "./CloudCostHelp";
import { invoiceItemLabel } from "./cloud-cost-labels";
import { overlapsServiceMonth } from "./cloud-cost-service-month";
export function CloudCostInvoices({
  report,
  provider,
}: { report: CloudCostReport; provider: string }): React.JSX.Element {
  const [complete, setComplete] = useState(false);
  const invoices = report.invoices.filter(
    (invoice) =>
      (provider === "all" || invoice.provider === provider) &&
      invoice.items.some((item) => overlapsServiceMonth(item, report.month)),
  );
  return (
    <section style={{ ...s.card, marginTop: 20 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 16,
        }}
      >
        <h3>
          {t("cost.invoice_details_short")}
          <CostHelp title={t("cost.invoice_details_short")}>
            <p>{t("cost.related_invoice_note")}</p>
          </CostHelp>
        </h3>
        <label style={s.cardLabel}>
          <input
            type="checkbox"
            checked={complete}
            onChange={(event) => setComplete(event.target.checked)}
          />{" "}
          {t("cost.full_invoice_toggle")}
        </label>
      </div>
      <InvoiceTable
        invoices={invoices}
        month={report.month}
        complete={complete}
      />
    </section>
  );
}
function InvoiceTable({
  invoices,
  month,
  complete,
}: {
  invoices: CloudCostInvoice[];
  month: string;
  complete: boolean;
}): React.JSX.Element {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={tableStyle}>
        <InvoiceColumns />
        <tbody>
          {invoices.flatMap((invoice) =>
            invoiceGroups(invoice, month, complete).map((group) => (
              <InvoiceGroup
                key={`${invoice.provider}:${invoice.id}:${group.period}`}
                invoice={invoice}
                group={group}
              />
            )),
          )}
          {!invoices.length && (
            <tr>
              <td colSpan={2} style={cellStyle}>
                {t("cost.no_invoice_short")}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
type InvoiceGroupData = { period: string; items: CloudCostInvoice["items"] };
function invoiceGroups(
  invoice: CloudCostInvoice,
  month: string,
  complete: boolean,
): InvoiceGroupData[] {
  const groups = new Map<string, CloudCostInvoice["items"]>();
  for (const item of invoice.items.filter(
    (item) => complete || overlapsServiceMonth(item, month),
  )) {
    const period =
      item.service_start && item.service_end
        ? `${item.service_start.slice(0, 10)} → ${item.service_end.slice(0, 10)}`
        : t("cost.invoice_level_short");
    groups.set(period, [...(groups.get(period) ?? []), item]);
  }
  return [...groups].map(([period, items]) => ({ period, items }));
}
function InvoiceGroup({
  invoice,
  group,
}: { invoice: CloudCostInvoice; group: InvoiceGroupData }): React.JSX.Element {
  return (
    <>
      <tr>
        <th colSpan={2} style={cellStyle}>
          {invoice.provider === "neon" ? "Neon" : "Cloudflare"} · {group.period}
          <CostHelp title={t("cost.invoice_details_short")}>
            <p>
              {t("cost.invoice_number")}: {invoice.id}
            </p>
            <p>
              {t("cost.issued")}: {invoice.issued_at.slice(0, 10)}
            </p>
            <p>
              {t("cost.source_short")}: {invoice.source}
            </p>
          </CostHelp>
        </th>
      </tr>
      {group.items.map((item, index) => (
        <tr key={`${index}:${item.description}`}>
          <td style={cellStyle}>
            {invoiceItemLabel(item.description)}
            <CostHelp title={invoiceItemLabel(item.description)}>
              <p>{item.description}</p>
              <p>
                {t("cost.charge_kind")}:{" "}
                {t(
                  `cost.${item.charge_type === "fixed" ? "fixed" : item.charge_type === "tax" ? "tax" : item.charge_type === "credit" ? "credit" : "variable"}`,
                )}
              </p>
            </CostHelp>
          </td>
          <td style={{ ...cellStyle, textAlign: "right" }}>
            {invoice.currency === "USD" ? "$" : `${invoice.currency} `}
            {item.amount.toFixed(2)}
          </td>
        </tr>
      ))}
    </>
  );
}
const cellStyle: React.CSSProperties = {
  padding: "12px 8px",
  borderBottom: "1px solid var(--color-border)",
  whiteSpace: "nowrap",
};

function InvoiceColumns(): React.JSX.Element {
  const columns = ["invoice_item", "fee_short"];
  return (
    <thead>
      <tr>
        {columns.map((key) => (
          <th key={key} style={cellStyle}>
            {t(`cost.${key}`)}
          </th>
        ))}
      </tr>
    </thead>
  );
}
const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  textAlign: "left",
  fontSize: 13,
};
